import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import Button from '@material-ui/core/Button';
import { CircularProgress } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import compose from 'helpers/compose';
import YearsAndMonths from 'components/Generic/YearsAndMonths';
import FormTextField from 'components/FormFields/FormTextField';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormDateSelects from 'components/FormFields/FormDateSelects';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import Switch from 'components/FormFields/Switch';
import useStyles from 'pages/Accounts/Edit/components/styles/moduleStyles';
import CalcIcon from 'components/Icons/CalcIcon';
import { getYearsAndMonthsFromLuxon, sqlDateToYearAndMonth, yearAndMonthToSql } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';

import { YearAndMonth } from 'common/interfaces';
import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import {
  ExternalProductData,
  ExternalProductOptions,
  ExtProductCategory,
  ExtProductFreq,
  ExtProductMode,
  ExtProductType,
  ExtProductUsage,
} from './model';
import { Currency } from 'enums';

interface OwnProps {
  currency: Currency;
  productId?: string;
  options: ExternalProductOptions;
  formikProps: FormikProps<ExternalProductData>;
  updateFinInst: () => void;
  minDate?: YearAndMonth;
  maxDate?: YearAndMonth;
  setModel: (model: boolean) => void;
}

interface Stores {
  extProductsStore: ExternalProductsStore;
}

type Props = OwnProps & Stores;

const ExternalProductModalTermination: React.FC<Props> = ({
  productId,
  extProductsStore,
  currency,
  options,
  formikProps,
  updateFinInst,
  minDate,
  maxDate,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showDetail, setShowDetail] = useState(formikProps.values.inflation.enabled === true);
  const { values } = formikProps;

  const changeUntilMonth = (formikProps: FormikProps<ExternalProductData>, untilYearsValues?: number[]) => {
    const {
      values: { frequency, since, until },
    } = formikProps;
    let monthToDisabled = false;

    if (frequency === ExtProductFreq.REPEAT_EVERY || frequency === ExtProductFreq.YEARLY) {
      monthToDisabled = true;

      if (since) {
        const { years: sinceYear, months: sinceMonth } = sqlDateToYearAndMonth(since);
        const dateUntil = sqlDateToYearAndMonth(until);

        if (sinceMonth !== dateUntil.months) {
          let newSinceYear = dateUntil.years || (untilYearsValues && untilYearsValues[0]) || sinceYear + 1;
          const newUntil = yearAndMonthToSql(newSinceYear, sinceMonth);

          formikProps.setFieldValue('until', newUntil, false);
        }
      }
    }
    return monthToDisabled;
  };

  const getUntilYearsValues = (formikProps: FormikProps<ExternalProductData>): number[] | undefined => {
    const {
      values: { frequency, since, repeatYears },
    } = formikProps;
    const { maxDate } = options;

    if (frequency === ExtProductFreq.REPEAT_EVERY && since && repeatYears) {
      //const repeatValue = parseInt(repeatYears);
      const repeatValue = repeatYears;
      const { years: sinceYear } = sqlDateToYearAndMonth(since);
      const { years: maxYear } = sqlDateToYearAndMonth(maxDate);

      const yearsValues = [];
      for (let year = sinceYear + repeatValue; year <= repeatValue * 20 + sinceYear; year = year + repeatValue) {
        if (year > maxYear) break;
        yearsValues.push(year);
      }
      return yearsValues;
    }
    return undefined;
  };

  const controlUntilYearValue = async (
    formikProps: FormikProps<ExternalProductData>,
    untilYearsValues: number[] | undefined,
  ) => {
    const {
      values: { until, since },
    } = formikProps;

    if (until) {
      const { years: untilYear, months: untilMonth } = sqlDateToYearAndMonth(until);

      // control if new year value is in untilYearsValues
      if (untilYearsValues && !untilYearsValues.includes(untilYear)) {
        const newUntil = yearAndMonthToSql(untilYearsValues[0], untilMonth);
        await formikProps.setFieldValue('until', newUntil);
        return;
      }

      const { years: sinceYear, months: sinceMonth } = sqlDateToYearAndMonth(since);

      // control if year until is not smaller then since year
      if (sinceYear > untilYear) {
        const newUntil = yearAndMonthToSql((untilYearsValues && untilYearsValues[0]) || sinceYear + 1, untilMonth);
        await formikProps.setFieldValue('until', newUntil);
        return;
      }
      if (sinceYear === untilYear && sinceMonth > untilMonth) {
        const newUntil = yearAndMonthToSql(sinceYear, sinceMonth + 1);
        await formikProps.setFieldValue('until', newUntil);
        return;
      }
    }
  };

  const untilYearsValues = getUntilYearsValues(formikProps);
  const monthUntilDisabled = changeUntilMonth(formikProps, untilYearsValues);

  const mobile = useMediaQuery('(max-width:500px)', { noSsr: true });

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={6}>
          {values.category === ExtProductCategory.PROPERTY ? (
            <Switch
              name={`useTarget`}
              label={t('externalProduct_useTarget')}
              afterChange={() =>
                formikProps.setFieldValue(
                  'frequency',
                  values.useTarget ? ExtProductFreq.SINGLE : ExtProductFreq.INFINITE,
                )
              }
            />
          ) : values.category !== ExtProductCategory.LOAN ? (
            <FormSelectField
              name="frequency"
              label={t('externalProduct_freq')}
              options={options.frequency[values.type].map((freq) => ({
                value: freq,
                label: t(`externalProduct_freq_${freq}`),
                tooltip: [ExtProductFreq.ADVANCE_PENSION, ExtProductFreq.PENSION].includes(freq)
                  ? t(`externalProduct_freq_${freq}_tooltip`)
                  : freq === ExtProductFreq.SINGLE && values.type === ExtProductType.RETIREMENT
                  ? t('externalProduct_freq_SINGLE_RETIREMENT_tooltip')
                  : undefined,
              }))}
              fast={false}
              afterChange={async () => extProductsStore.model(values)}
            />
          ) : (
            ''
          )}
        </Grid>

        {values.category === ExtProductCategory.PROPERTY && values.useTarget && (
          <>
            <Grid item xs={12} />
            <Grid item xs={12} sm={3} style={{ fontSize: '16px' }}>
              {t('externalProduct_sellDate')}:
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormDateSelects
                name={`since`}
                minDate={minDate}
                maxDate={maxDate}
                fast={false}
                afterChange={async () => await controlUntilYearValue(formikProps, untilYearsValues)}
              />
            </Grid>
          </>
        )}

        {((values.category === ExtProductCategory.PROPERTY && values.useTarget) ||
          (values.category !== ExtProductCategory.PROPERTY && values.frequency !== ExtProductFreq.INFINITE)) && (
          <>
            <Grid container item xs={12} sm={6} alignItems="center">
              {[ExtProductFreq.PENSION, ExtProductFreq.ADVANCE_PENSION, ExtProductFreq.REPEAT_EVERY].includes(
                values.frequency,
              ) ? (
                <>
                  {[ExtProductFreq.PENSION, ExtProductFreq.ADVANCE_PENSION].includes(values.frequency) && (
                    <Grid item xs={12}>
                      <FormNumberField name="children" label={t('externalProduct_children')} />
                    </Grid>
                  )}
                  {values.frequency === ExtProductFreq.REPEAT_EVERY && (
                    <>
                      <Grid item xs={8} sm={6}>
                        <FormSelectField
                          name="repeatYears"
                          label={t('general_Count')}
                          options={new Array(options.maxYears - options.minYears + 1).fill(0).map((year, i) => ({
                            value: `${i + options.minYears}`,
                            label: `${i + options.minYears}`,
                          }))}
                          afterChange={() => {
                            setTimeout(() => {
                              const value = untilYearsValues && untilYearsValues[0];
                              const {
                                values: { until },
                              } = formikProps;
                              const { months: sinceMonth } = sqlDateToYearAndMonth(until);

                              if (value) {
                                const newUntil = yearAndMonthToSql(value, sinceMonth);
                                formikProps.setFieldValue('until', newUntil);
                              }
                            }, 0);
                          }}
                        />
                      </Grid>
                      <Grid item xs={4} sm={6}>
                        {t('general_repeatYears')}
                      </Grid>
                    </>
                  )}
                </>
              ) : (
                ''
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormSelectField
                name="usage"
                label={t('externalProduct_usage')}
                options={options.usage[values.category].map((usage) => ({
                  value: usage,
                  label: t([`externalProduct_usage_${usage}_${values.category}`, `externalProduct_usage_${usage}`]),
                }))}
                fast={false}
                afterChange={async () => {
                  extProductsStore.model(values);
                  values.category === ExtProductCategory.LOAN &&
                    (await extProductsStore.calculateLoan(values, formikProps));
                }}
                tooltipContent={t(
                  `externalProduct_usage_tooltip${values.category === ExtProductCategory.LOAN ? '_LOAN' : ''}`,
                )}
              />
            </Grid>

            {values.usage !== ExtProductUsage.NONE && (
              <>
                {values.category === ExtProductCategory.LOAN && (
                  <>
                    <Grid item xs={12} sm={6} />
                    <Grid item xs={12} sm={6}>
                      <FormSelectField
                        name="frequency"
                        label={t('externalProduct_freq')}
                        options={options.frequency[values.type].map((freq) => ({
                          value: freq,
                          label: t(`externalProduct_freq_${freq}`),
                        }))}
                        fast={false}
                        afterChange={async () => extProductsStore.model(values)}
                      />
                    </Grid>
                  </>
                )}

                {values.usage === ExtProductUsage.EXTERNAL && (
                  <Grid item xs={12} sm={6}>
                    <FormTextField name="targetTitle" label={t('externalProduct_targetTitle')} />
                  </Grid>
                )}
                {values.usage === ExtProductUsage.TARGET && <Grid item xs={6} className={classes.xsHide} />}

                {values.category !== ExtProductCategory.PROPERTY ? (
                  <Grid item xs={12} sm={6} md={3}>
                    <GridSubtitle noMargin>
                      {t(values.frequency === ExtProductFreq.SINGLE ? 'general_When' : 'general_Since')}:
                    </GridSubtitle>
                    <FormDateSelects
                      name={`since`}
                      minDate={minDate}
                      maxDate={maxDate}
                      fast={false}
                      afterChange={async () => await controlUntilYearValue(formikProps, untilYearsValues)}
                    />
                  </Grid>
                ) : (
                  ''
                )}

                {[
                  ExtProductFreq.MONTHLY,
                  ExtProductFreq.YEARLY,
                  ExtProductFreq.REPEAT_EVERY,
                  ExtProductFreq.PENSION,
                  ExtProductFreq.ADVANCE_PENSION,
                ].includes(values.frequency) && (
                  <Grid item xs={12} sm={6} md={3}>
                    <GridSubtitle noMargin>
                      {t('general_Until')}:
                      {[ExtProductFreq.PENSION, ExtProductFreq.ADVANCE_PENSION].includes(values.frequency) && (
                        <Info subtitle content={t('externalProduct_until_RETIREMENT_tooltip')} />
                      )}
                    </GridSubtitle>
                    <FormDateSelects
                      name={`until`}
                      minDate={
                        values.frequency === ExtProductFreq.ADVANCE_PENSION && values.since !== ''
                          ? getYearsAndMonthsFromLuxon(
                              DateTime.fromFormat(values.since, 'yyyy-MM-dd').plus({ years: 2 }),
                            )
                          : minDate
                      }
                      maxDate={maxDate}
                      fast={false}
                      afterChange={async () => await controlUntilYearValue(formikProps, untilYearsValues)}
                      readOnlyMonth={monthUntilDisabled}
                      yearsValues={untilYearsValues}
                    />
                  </Grid>
                )}

                {values.category !== ExtProductCategory.PROPERTY && (
                  <Grid item xs={12} sm={6} style={{ alignSelf: 'end', lineHeight: '40px' }}>
                    {values.retirementAge &&
                      [ExtProductFreq.PENSION, ExtProductFreq.ADVANCE_PENSION].includes(values.frequency) && (
                        <>
                          {t('finQuestionnary_aboutFamily_lawRetirenmentAge')}:{' '}
                          <YearsAndMonths data={values.retirementAge} />
                        </>
                      )}
                  </Grid>
                )}

                {(values.frequency === ExtProductFreq.SINGLE || values.category === ExtProductCategory.LOAN) && (
                  <>
                    {![ExtProductCategory.PROPERTY, ExtProductCategory.LOAN].includes(values.category) && (
                      <Grid item xs={6} md={3} className={classes.smHide} />
                    )}
                    <Grid item xs={6} md={3}>
                      <FormSelectField
                        name="mode"
                        label={t('externalProduct_mode')}
                        options={[ExtProductMode.CALCULATED, ExtProductMode.MANUAL].map((mode) => ({
                          value: mode,
                          label: t(`externalProduct_mode_${mode}`),
                        }))}
                        afterChange={async () =>
                          formikProps.values.mode === ExtProductMode.CALCULATED &&
                          (await extProductsStore.calculateLoan(values, formikProps))
                        }
                      />
                    </Grid>
                  </>
                )}

                <Grid
                  item
                  xs={
                    values.frequency === ExtProductFreq.SINGLE || values.category === ExtProductCategory.LOAN ? 6 : 12
                  }
                  md={values.frequency === ExtProductFreq.SINGLE || values.category === ExtProductCategory.LOAN ? 3 : 6}
                >
                  <FormNumberField
                    name="value.value"
                    label={t(
                      `externalProduct_value${
                        values.frequency === ExtProductFreq.SINGLE && values.mode !== ExtProductMode.CALCULATED
                          ? ''
                          : '_calculated'
                      }`,
                    )}
                    endAdornment={t(`currency_${currency}`)}
                    disabled={values.mode === ExtProductMode.CALCULATED}
                    fast={false}
                    tooltipContent={
                      values.frequency === ExtProductFreq.SINGLE
                        ? undefined
                        : t('externalProduct_value_calculated_tooltip')
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  {values.mode === ExtProductMode.CALCULATED && values.category !== ExtProductCategory.LOAN && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        extProductsStore.calculateValue(values, formikProps);
                      }}
                      startIcon={
                        extProductsStore.calculatingValue ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <CalcIcon fontSize="medium" transparentBgr />
                        )
                      }
                      disabled={extProductsStore.calculatingValue || values.since === ''}
                    >
                      {t('externalProduct_calculateValue')}
                    </Button>
                  )}
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>

      {values.frequency !== ExtProductFreq.INFINITE && values.usage !== ExtProductUsage.NONE && (
        <>
          <Link
            className={classes.detailLink}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setShowDetail(!showDetail);
            }}
          >
            <span className={classes.detailControl}>
              {t('general_advanced')}
              <DropDownIcon className={`${showDetail ? classes.shownIcon : ''}`} />
            </span>
          </Link>

          <Collapse in={showDetail}>
            <Grid container>
              <Grid item xs={12}>
                {mobile && (
                  <>
                    {t('general_useInflation')} <Info content={t('deposits_inflation_tooltip')} inline />
                  </>
                )}
              </Grid>
              <Grid item xs={8} sm={6} style={mobile ? { maxWidth: '25%' } : undefined}>
                <Switch
                  name={`inflation.enabled`}
                  label={mobile ? undefined : t('general_useInflation')}
                  tooltipContent={mobile ? undefined : t('deposits_inflation_tooltip')}
                  afterChange={() => {
                    if (!values.inflation.enabled) {
                      formikProps.setFieldValue('inflation.valuation', false);
                      formikProps.setFieldValue('inflation.type', '');
                    }
                  }}
                  disabled // EDS-4921
                />
              </Grid>
              <Grid item xs={4} sm={4} md={2} style={mobile ? { maxWidth: '75%', flexBasis: '75%' } : undefined}>
                <FormSelectField
                  name="inflation.rate"
                  label={t('general_annualInflation')}
                  options={Array(20)
                    .fill(0)
                    .map((_x, i) => ({
                      value: `${(i + 1) / 100}`,
                      label: `${i + 1} %`,
                    }))}
                  fast={false}
                  disabled={!values.inflation.enabled}
                />
              </Grid>
              <Grid item xs={12}>
                {mobile && (
                  <>
                    {t('deposits_useInflation')} <Info content={t('deposits_useInflation_tooltip')} inline />
                  </>
                )}
              </Grid>
              <Grid item xs={8} sm={6} style={mobile ? { maxWidth: '25%' } : undefined}>
                <Switch
                  name={`inflation.valuation`}
                  label={mobile ? undefined : t('deposits_useInflation')}
                  tooltipContent={mobile ? undefined : t('deposits_useInflation_tooltip')}
                  disabled={!values.inflation.enabled}
                />
              </Grid>
              <Grid item xs={4} sm={4} md={2} style={mobile ? { maxWidth: '75%', flexBasis: '75%' } : undefined}>
                <FormSelectField
                  name="inflation.type"
                  options={options.inflationType.map((type) => ({
                    value: type,
                    label: t(`general_inflationType_${type}`),
                  }))}
                  fast={false}
                  disabled={!values.inflation?.enabled || !values.inflation?.valuation}
                  tooltipContent={t('deposits_useInflationType_tooltip')}
                />
              </Grid>
            </Grid>
          </Collapse>
        </>
      )}
    </>
  );
};

export default compose(observer)(ExternalProductModalTermination);
